<template>
	<div class="w-100">
		<slot name="header"></slot>
		<div
				:id="`avatars-${invite.id}-${_uid}`"
				:ref="`avatars-${invite.id}-${_uid}`"
				class="align-center d-flex invitee-avatars justify-center w-100"
				:class="{ 
				'invitee-avatars--dark': dark,
				'grayscale': grayscale
			}"
		>
			<template v-for="(invitee, idx) in invitees">
				<v-tooltip
						v-if="idx < avatarsShownCount"
						:key="`header-avatar-${invitee.id}`"
						bottom
						color="black"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-avatar
								v-bind="attrs"
								:class="{
								active: activeAvatar >= 0 && activeAvatar === idx,
								'elevation-16': activeAvatar >= 0 && activeAvatar === idx
							}"
								:color="hasAvatarMedia(invitee.media) ? 'grey lighten-2' : avatarColors[invitee.id]"
								:size="
							activeAvatar >= 0 && activeAvatar === idx
								? avatarSize * avatarActiveScale
								: avatarSize
						"
								:style="{
							marginLeft: idx > 0 ? `-${overlap}px` : '',
							borderColor: border ? `${border} !important` : ''
						}"
								v-on="on"
								@click="$emit(clickHandler, idx)"
						>
							<img
									v-if="hasAvatarMedia(invitee.media)"
									:id="invitee.id"
									:alt="invitee.name"
									:src="getAvatarMedia(invitee.media)"
							/>
							<span v-else class="initial">
							{{ invitee.name.charAt(0) }}
						</span>
						</v-avatar>
					</template>
					<span>{{ invitee.name }}</span>
				</v-tooltip>
			</template>
			<span
					v-if="showMoreCount && moreCount"
					:ref="`avatars-count-${invite.id}-${_uid}`"
					class="font-weight-bold grey--text text-caption"
					:class="{
						'text--darken-2': !dark,
						'text--lighten-2': dark
					}"
			>
				+{{ moreCount }}
			</span>
		</div>
		<slot name="footer"></slot>
	</div>
</template>

<script>
import { inviteHelpers } from '../helpers';

export default {
	name: 'InviteAvatars',
	props: {
		activeAvatar: {
			type: Number,
			default: -1
		},
		border: {
			type: String
		},
		clickHandler: {
			type: String,
			default: 'set-active-avatar'
		},
		dark: {
			type: Boolean,
			default: false
		},
		grayscale: Boolean,
		overlap: {
			type: Number,
			default: 16
		},
		avatarSize: {
			type: Number,
			default: 48
		},
		imagesOnly: {
			type: Boolean,
			default: true
		},
		invite: {
			type: Object,
			default: () => ({})
		},
		invitees: {
			type: Array,
			default: () => []
		},
		maxAvatars: {
			type: [Number, String],
			default: 0
		},
		mediaLocations: {
			type: Array,
			default: () => ['Square', 'Header']
		},
		noDeclines: Boolean,
		showMoreCount: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			avatarActiveScale: 1.33333333333,
			avatarsContainerWidth: 0,
			avatarColors: {},
			inviteHelpers,
			moreCountContainerWidth: 0,
			uid: undefined
		};
	},
	computed: {
		avatarsContainer() {
			const { invite, uid } = this;
			return this.$refs?.[`avatars-${invite.id}-${uid}`];
		},
		avatarsShownCount() {
			const {
				avatarActiveScale,
				avatarsContainerWidth,
				avatarSize,
				invitees,
				inviteesWithMedia,
				maxAvatars,
				moreCountContainerWidth,
				overlap
			} = this;

			const activeWidth = avatarSize * avatarActiveScale - avatarSize;
			const fitToWidth = avatarsContainerWidth - moreCountContainerWidth - activeWidth;
			const avatarsFitMax = Math.floor((fitToWidth - overlap) / (avatarSize - overlap));

			// In case there are no venues with media we should set this to max
			// and then show avatars as an icon/first initial
			const hasMediaCount = inviteesWithMedia.length > 0
					? inviteesWithMedia.length
					: invitees.length;

			return maxAvatars === 0
					? Math.min(avatarsFitMax, hasMediaCount)
					: Math.min(avatarsFitMax, hasMediaCount, maxAvatars);
		},
		inviteesWithMedia() {
			return this.invitees.filter(invitee => {
				const hasMedia = this.hasAvatarMedia(invitee.media, this.mediaLocations);
				return !this.noDeclines
						? hasMedia
						: hasMedia && invitee.statusCode !== 'declined';
			});
		},
		moreCount() {
			const totalInvitees = this.invitees.length;
			if(!this.maxAvatars) return totalInvitees - this.avatarsShownCount;

			return totalInvitees - Math.min(this.avatarsShownCount, this.maxAvatars);
		},
		moreCountContainer() {
			const { invite, uid } = this;
			return this.$refs?.[`avatars-count-${invite.id}-${uid}`];
		}
	},
	methods: {
		getAvatarMedia(media = [], targetLocations = this.mediaLocations) {
			if(!this.hasAvatarMedia(media, targetLocations)) return false;

			const avatarMedia = [];
			targetLocations.forEach(location => {
				const index = media.findIndex(m => m.location === location);
				if(index > -1) avatarMedia.push(media[index].url);
			});
			return avatarMedia[0];
		},
		hasAvatarMedia(media = [], targetLocations = this.mediaLocations) {
			return media.length && media.some(m => targetLocations.includes(m.location));
		},
		setAvatarsContainerWidth() {
			const { avatarsContainer } = this;
			this.avatarsContainerWidth = avatarsContainer
					? avatarsContainer.getBoundingClientRect().width
					: 0;
		},
		setMoreCountContainerWidth() {
			const { moreCountContainer, showMoreCount } = this;
			this.moreCountContainerWidth =
					moreCountContainer && showMoreCount
							? moreCountContainer.getBoundingClientRect().width + 2
							: 0;
		}
	},
	beforeDestroy() {
		if(typeof window === 'undefined') return;

		window.removeEventListener(
				'resize',
				() => {
					this.setAvatarsContainerWidth();
					this.setMoreCountContainerWidth();
				},
				{ passive: true }
		);
	},
	created() {
		this.invitees.forEach(i => this.avatarColors = {
			...this.avatarColors,
			[i.id]: `hsl(${_.random(0, 360)}deg, 70%, 50%)` // hsl(7.32deg 80.39% 70%)
		});
	},
	mounted() {
		// eslint-disable-next-line no-underscore-dangle
		this.uid = this._uid;
		// this.setAvatarsContainerWidth();
		// this.setMoreCountContainerWidth();
		// setInterval(() => {
		//	 this.setAvatarsContainerWidth();
		//	 this.setMoreCountContainerWidth();
		// }, 1000);
		setTimeout(() => {
			this.setAvatarsContainerWidth();
			this.setMoreCountContainerWidth();
		}, 100);

		setInterval(() => {
			this.setAvatarsContainerWidth();
			this.setMoreCountContainerWidth();
		}, 100);

		window.addEventListener(
				'resize',
				() => {
					this.setAvatarsContainerWidth();
					this.setMoreCountContainerWidth();
				},
				{ passive: true }
		);
	}
};
</script>

<style lang="scss" scoped>
.v-application {
	& .invitee-avatars {
		& .v-avatar {
			border-color: white !important;
			border-style: solid !important;
			border-width: 2px !important;
			cursor: pointer;
			position: relative;

			&:hover,
			&.active {
				filter: none;
				z-index: 1000 !important; // One more than maxHeaderAvatars
			}
		}

		&.grayscale {
			& .v-avatar {
				filter: grayscale(1);

				&:hover,
				&.active {
					filter: none;
				}
			}
		}

		&--dark {
			& .v-avatar {
				border-color: black !important;
			}
		}
	}

	& .initial {
		color: white;
		font-size: 1.25rem;
		font-weight: 500;
		text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.38);
	}
}
</style>
